import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import seoAiService, { InternalLinkFinderResult } from '@/services/seoAi';
import seoAiCacheService from '@/services/seoAiCache';

type Props = {
  keyword: string;
  keywordId: string;
  projectId: string;
  targetPage: string;
  sitePages: Array<{ page: string; keyword?: string }>;
  cachedResult?: InternalLinkFinderResult | null;
};

const formatPath = (url: string): string => {
  try {
    const u = new URL(url);
    return u.pathname + (u.search || '');
  } catch {
    return url.replace(/^https?:\/\/[^/]+/, '') || '/';
  }
};

const relevanceColor = (score: number): string => {
  if (score >= 80) return '#0D9D58';
  if (score >= 60) return '#F4B400';
  return '#EA4335';
};

const InternalLinkFinder = function ({
  keyword,
  keywordId,
  projectId,
  targetPage,
  sitePages,
  cachedResult,
}: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<InternalLinkFinderResult | null>(cachedResult ?? null);
  const [error, setError] = useState<string | null>(null);

  const handleFind = async () => {
    if (!targetPage || sitePages.length === 0) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await seoAiService.findInternalLinks({
        keyword,
        targetPage,
        sitePages,
      });
      setResult(data);

      await seoAiCacheService.saveInternalLinkSuggestions({
        projectId,
        keywordId,
        targetPage,
        suggestions: data,
        status: 'complete',
      }).catch(() => {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to find internal links');
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = !targetPage || sitePages.length === 0;

  if (!result && !isLoading) {
    return (
      <Box>
        <Button
          size="small"
          startIcon={<LinkIcon />}
          onClick={handleFind}
          disabled={isDisabled}
          sx={{ fontSize: 12, textTransform: 'none', color: isDisabled ? 'text.disabled' : '#054AD9', mt: 0.5 }}
        >
          Find Internal Link Opportunities
        </Button>
        {isDisabled && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {!targetPage
              ? 'No ranking page available for this keyword.'
              : 'No site pages available from GSC data.'}
          </Typography>
        )}
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{error}</Typography>
        )}
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, py: 2 }}>
        <CircularProgress size={16} sx={{ color: '#054AD9' }} />
        <Typography variant="caption" color="text.secondary">Analyzing site pages for internal link opportunities...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="error">{error}</Typography>
        <Button size="small" onClick={handleFind} sx={{ fontSize: 11, ml: 1 }}>Retry</Button>
      </Box>
    );
  }

  const { suggestions, summary } = result!;

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
        <LinkIcon sx={{ fontSize: 16, color: '#054AD9' }} />
        <Typography variant="caption" fontWeight={700} color="#054AD9">Internal Link Opportunities</Typography>
        <Tooltip title="Re-analyze">
          <IconButton size="small" onClick={handleFind} sx={{ ml: 'auto', p: 0.5 }}>
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">Total Opportunities</Typography>
          <Typography variant="h6" fontWeight={700}>{summary?.totalOpportunities ?? suggestions?.length ?? 0}</Typography>
        </Box>
        <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">High Priority</Typography>
          <Typography variant="h6" fontWeight={700} color="#0D9D58">{summary?.highPriority ?? 0}</Typography>
        </Box>
      </Box>

      {/* Suggestions table */}
      {suggestions?.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>From Page</TableCell>
                <TableCell>Anchor Text</TableCell>
                <TableCell align="center">Relevance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suggestions.map((s, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.fromPage}>
                      {formatPath(s.fromPage)}
                    </Typography>
                    {s.context && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block' }}>
                        {s.context.slice(0, 80)}{s.context.length > 80 ? '...' : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={s.anchorText} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${s.relevanceScore}%`}
                      size="small"
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#fff',
                        backgroundColor: relevanceColor(s.relevanceScore),
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(!suggestions || suggestions.length === 0) && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No internal link opportunities found.
        </Typography>
      )}
    </Box>
  );
};

export default InternalLinkFinder;
